/**
 * the implicit "always open" time table
 *
 * it is not stored in the timeTables setting, it exists everywhere a time table can be
 * selected: pickup locations, delivery zones and production times can point at it to say
 * "no time limitation at all" without anybody having to maintain a table that is open
 * around the clock.
 *
 * resolve time table ids with findTimeTable() (or OrderCalculator#getTimeTableById, which
 * uses it) so the implicit table is handled the same way everywhere.
 */
import { v4 as uuid } from 'uuid';
export const ALWAYS_OPEN_TIME_TABLE_ID = 'alwaysOpen';
//delivery zones without a time table used this marker before the always open table
//existed — it resolves to the very same hours
export const UNRESTRICTED_TIME_TABLE_ID = '*unrestricted*';
//the name is data, not a ui translation: the admin dashboard, the customer app and the
//server side all display it, and none of them shares a translation file with the others
export const ALWAYS_OPEN_TIME_TABLE_NAME = {
    de: '24/7',
    en: '24/7',
    es: '24/7',
    ca: '24/7',
    fr: '24/7',
    nl: '24/7',
    zh: '24/7',
};
/**
 * every weekday from 00:00 to 24:00
 */
export function alwaysOpenTimeTableRules() {
    let rules = [];
    for (let weekday = 0; weekday < 7; weekday++) {
        rules.push({
            fixed: true,
            occurrence: 'periodic',
            weekday,
            date: null,
            hours: [{ start: '00:00', end: '24:00' }],
        });
    }
    return rules;
}
export function isAlwaysOpenTimeTableId(timeTableId) {
    return timeTableId == ALWAYS_OPEN_TIME_TABLE_ID || timeTableId == UNRESTRICTED_TIME_TABLE_ID;
}
/**
 * a fresh copy of the implicit table, shaped like a stored one (fixed: it can neither be
 * edited nor deleted, implicit: it is not part of the timeTables setting)
 */
export function alwaysOpenTimeTable() {
    return {
        id: ALWAYS_OPEN_TIME_TABLE_ID,
        implicit: true,
        fixed: true,
        name: { ...ALWAYS_OPEN_TIME_TABLE_NAME },
        rules: alwaysOpenTimeTableRules(),
    };
}
/**
 * resolves a time table id against the company's stored time tables, transparently
 * handling the implicit always open table. returns null for an unknown id.
 */
export function findTimeTable(timeTables, timeTableId) {
    if (!timeTableId) {
        return null;
    }
    if (isAlwaysOpenTimeTableId(timeTableId)) {
        return alwaysOpenTimeTable();
    }
    if (!Array.isArray(timeTables)) {
        return null;
    }
    return timeTables.find((timeTable) => timeTable && timeTable.id == timeTableId) || null;
}
/**
 * the sentinel a select offers for "an own time table": choosing it creates a local table
 * (with a real id) on the delivery zone / pickup location it belongs to
 */
export const LOCAL_TIME_TABLE_ID = 'local';
/**
 * the keys the owners of a local time table store it under. The owner keeps both the id
 * (pickupTimeTableId / deliveryTimeTableId — the same field a global table is selected in)
 * and the table itself, so nothing else has to know where the table came from.
 */
export const LOCAL_TIME_TABLE_KEYS = {
    pickupLocations: 'pickupTimeTable',
    deliveryZones: 'deliveryTimeTable',
};
/**
 * a local time table, shaped exactly like a stored one so the same editors can be reused
 * (local: it lives on its owner instead of in the timeTables setting)
 */
export function createLocalTimeTable() {
    return {
        id: uuid(),
        local: true,
        name: {},
        rules: alwaysOpenTimeTableRules(),
    };
}
export function isLocalTimeTable(timeTable) {
    return Boolean(timeTable && timeTable.local);
}
function sectionOf(company, key) {
    if (!company) {
        return [];
    }
    if (Array.isArray(company[key])) {
        return company[key];
    }
    if (company.settings && Array.isArray(company.settings[key])) {
        return company.settings[key];
    }
    return [];
}
/**
 * every local time table of a company, collected from the delivery zones and pickup
 * locations that own one. Accepts a company (public infos) as well as a settings object.
 */
export function localTimeTablesOf(company) {
    let timeTables = [];
    for (let section in LOCAL_TIME_TABLE_KEYS) {
        let key = LOCAL_TIME_TABLE_KEYS[section];
        for (let owner of sectionOf(company, section)) {
            let timeTable = owner ? owner[key] : null;
            if (timeTable && timeTable.id && Array.isArray(timeTable.rules)) {
                timeTables.push(timeTable);
            }
        }
    }
    return timeTables;
}
/**
 * every time table an id can point at: the global ones plus the local ones
 */
export function resolvableTimeTables(company) {
    return [...sectionOf(company, 'timeTables'), ...localTimeTablesOf(company)];
}
/**
 * resolves a time table id against everything a company has — global tables, local tables
 * and the implicit always open one
 */
export function findTimeTableForCompany(company, timeTableId) {
    return findTimeTable(resolvableTimeTables(company), timeTableId);
}
/**
 * the stored time tables plus the implicit one — the list to offer wherever a time table
 * can be selected
 */
export function selectableTimeTables(timeTables) {
    let selectable = Array.isArray(timeTables) ? timeTables.filter((timeTable) => Boolean(timeTable)) : [];
    return [...selectable, alwaysOpenTimeTable()];
}
